/** @jsxImportSource @emotion/react */
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AppBar from "../../components/AppBar";
import Button from "../../components/Button";
import TextBox from "../../components/TextBox";
import Dropdown from "../../components/Dropdown";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import Previous from "../../assets/images/previous.png";
import signUpSVG from "../../assets/images/illustrations/signUp.svg";
import { COUNTRY1 } from "../../constants";
import { baseUrl } from "../../connections";
import { UserContext } from "../../context/UserContext";
import { useCookies } from "react-cookie";
import {
  isProfileCompletionPending,
  persistUserSession,
} from "../../utils/authSession";
import { useGetSignupScreenStyles } from "../SignUp/index.styles";
import AddressPickerModal from "../../components/address-picker/AddressPickerModal";

const USER_FILE_BASE_URL = baseUrl.replace(/\/api\/v1\/?$/, "");

const buildProfileImageUrl = (value) => {
  if (!value) {
    return "";
  }

  if (
    String(value).startsWith("http://") ||
    String(value).startsWith("https://") ||
    String(value).startsWith("data:")
  ) {
    return value;
  }

  return `${USER_FILE_BASE_URL}/user/file/${value}`;
};

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () =>
      reject(new Error("Image preview could not be loaded."));
    reader.readAsDataURL(file);
  });

const readStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch (error) {
    return null;
  }
};

const readStoredToken = () => {
  try {
    return JSON.parse(localStorage.getItem("token") || '""');
  } catch (error) {
    return "";
  }
};

const getPhoneInputValue = (storedUser) => {
  const existingPhone = storedUser?.phone;

  if (existingPhone?.value) {
    return {
      code: String(existingPhone.code || "1").replace("+", ""),
      value: String(existingPhone.value || ""),
    };
  }

  const mobile = String(storedUser?.mobile || "").replace("+", "");

  if (!mobile) {
    return {
      code: "1",
      value: "",
    };
  }

  if (mobile.length <= 10) {
    return {
      code: "1",
      value: mobile,
    };
  }

  return mobile.length === 11
    ? {
        code: mobile.slice(0, 1),
        value: mobile.slice(1),
      }
    : {
        code: mobile.slice(0, 2),
        value: mobile.slice(2),
      };
};

const parseJsonResponse = async (response, fallbackMessage) => {
  const text = await response.text();

  if (response.status !== 200) {
    throw new Error(text || fallbackMessage);
  }

  return text ? JSON.parse(text) : { data: [] };
};

const fetchLocationList = async (path) => {
  const result = await fetch(`${baseUrl}${path}`);
  const payload = await parseJsonResponse(result, "Location data could not be loaded.");

  return Array.isArray(payload?.data) ? payload.data : [];
};

const fetchStatesByCountry = async (countryUuid) => {
  return fetchLocationList(`/state/list/by/country/${countryUuid}`);
};

const fetchCountiesByState = async (stateUuid) => {
  return fetchLocationList(`/county/list/by/state/${stateUuid}`);
};

const fetchCitiesByCounty = async (countyUuid) => {
  return fetchLocationList(`/city/list/by/county/${countyUuid}`);
};

const normalizePlaceName = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const normalizeCountyName = (value) =>
  normalizePlaceName(value)
    .replace(/\b(county|parish|borough|municipality|census area)\b/g, "")
    .replace(/\s+/g, " ")
    .trim();

const resolveExactCountyMatch = (counties, pickedCountyName) => {
  if (!Array.isArray(counties) || counties.length === 0) {
    return null;
  }

  const normalizedPickedCounty = normalizeCountyName(pickedCountyName);
  if (!normalizedPickedCounty) {
    return null;
  }

  return (
    counties.find(
      (county) => normalizeCountyName(county?.name) === normalizedPickedCounty,
    ) || null
  );
};

const resolveStateMatch = (states, pickedStateName, pickedStateCode = "") => {
  if (!Array.isArray(states) || states.length === 0) {
    return null;
  }

  const normalizedPickedState = normalizePlaceName(pickedStateName);
  const normalizedPickedCode = normalizePlaceName(pickedStateCode);

  if (normalizedPickedState) {
    const exactMatch = states.find(
      (state) => normalizePlaceName(state?.name) === normalizedPickedState,
    );
    if (exactMatch) {
      return exactMatch;
    }

    const partialMatch = states.find((state) => {
      const normalizedStateName = normalizePlaceName(state?.name);

      return (
        normalizedStateName.includes(normalizedPickedState) ||
        normalizedPickedState.includes(normalizedStateName)
      );
    });
    if (partialMatch) {
      return partialMatch;
    }
  }

  if (normalizedPickedCode) {
    const codeMatch = states.find((state) => {
      const normalizedStateCode = normalizePlaceName(
        state?.code || state?.short_name || state?.shortName || "",
      );

      return (
        normalizedStateCode && normalizedStateCode === normalizedPickedCode
      );
    });
    if (codeMatch) {
      return codeMatch;
    }
  }

  return states.length === 1 ? states[0] : null;
};

const resolveExactCityMatch = (cities, pickedCityName) => {
  if (!Array.isArray(cities) || cities.length === 0) {
    return null;
  }

  const normalizedPickedCity = normalizePlaceName(pickedCityName);
  if (!normalizedPickedCity) {
    return null;
  }

  return (
    cities.find(
      (city) => normalizePlaceName(city?.name) === normalizedPickedCity,
    ) || null
  );
};

const pickCityCandidate = (pickedAddress) => {
  if (!pickedAddress) return "";
  if (pickedAddress.city) return String(pickedAddress.city);

  // Fallback: displayLine2 is often like "Dallas, TX, 75201".
  const line2 = String(pickedAddress.displayLine2 || "");
  if (!line2) return "";

  const parts = line2
    .split(",")
    .map((part) => String(part || "").trim())
    .filter(Boolean);

  // Skip tokens that look like state codes or ZIPs.
  const candidate = parts.find((part) => {
    if (/^\d{5}(-\d{4})?$/.test(part)) return false;
    if (/^[a-z]{2}$/i.test(part)) return false;
    return true;
  });

  return candidate || "";
};

const buildCountyMasterName = (value) => {
  const cleaned = String(value || "")
    .replace(/\b(county|parish|borough|municipality|census area)\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();

  return cleaned || String(value || "").trim();
};

const parseApiBody = async (response) => {
  const text = await response.text();

  if (response.status !== 200) {
    throw new Error(text || "Location data could not be saved.");
  }

  return text ? JSON.parse(text) : {};
};

const createCounty = async (token, stateUuid, countyName) => {
  const response = await fetch(`${baseUrl}/county`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-auth-token": token,
    },
    body: JSON.stringify({
      state_uuid: stateUuid,
      name: buildCountyMasterName(countyName),
    }),
  });

  return parseApiBody(response);
};

const createCity = async (token, countyUuid, cityName) => {
  const response = await fetch(`${baseUrl}/city`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-auth-token": token,
    },
    body: JSON.stringify({
      county_uuid: countyUuid,
      name: String(cityName || "").trim(),
    }),
  });

  return parseApiBody(response);
};

const findOrCreateCounty = async (token, stateUuid, countyName) => {
  if (!stateUuid || !countyName) {
    return null;
  }

  const counties = await fetchCountiesByState(stateUuid);
  const existingCounty = resolveExactCountyMatch(counties, countyName);
  if (existingCounty?.uuid) {
    return existingCounty;
  }

  if (!token) {
    return null;
  }

  try {
    const created = await createCounty(token, stateUuid, countyName);
    return created?.data || null;
  } catch (error) {
    if (String(error?.message || "").includes("County already Available")) {
      const refreshed = await fetchCountiesByState(stateUuid);
      return resolveExactCountyMatch(refreshed, countyName);
    }

    throw error;
  }
};

const findOrCreateCity = async (token, countyUuid, cityName) => {
  if (!countyUuid || !cityName) {
    return null;
  }

  const cities = await fetchCitiesByCounty(countyUuid);
  const existingCity = resolveExactCityMatch(cities, cityName);
  if (existingCity?.uuid) {
    return existingCity;
  }

  if (!token) {
    return null;
  }

  try {
    const created = await createCity(token, countyUuid, cityName);
    return created?.data || null;
  } catch (error) {
    if (String(error?.message || "").includes("City Already added")) {
      const refreshed = await fetchCitiesByCounty(countyUuid);
      return resolveExactCityMatch(refreshed, cityName);
    }

    throw error;
  }
};

const hasProfileFieldValue = (value) => Boolean(String(value || "").trim());
const PROFILE_NAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9\s.'-]*$/;

const getProfileNameError = (value, label) => {
  const name = String(value || "").trim();

  if (!name) {
    return `Enter your ${label.toLowerCase()}.`;
  }

  if (name.length > 50) {
    return `${label} must be 50 characters or less.`;
  }

  if (!PROFILE_NAME_PATTERN.test(name)) {
    return `${label} can only include letters, numbers, spaces, apostrophes, periods, or hyphens.`;
  }

  return "";
};

const ProfileCompletion = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUserDetails } = useContext(UserContext);
  const [, setCookie] = useCookies([
    "dInfoUserEmail",
    "dInfoUserId",
    "dInfoUserType",
  ]);
  const [stateOptions, setStateOptions] = useState([]);
  const [pickedAddress, setPickedAddress] = useState(null);
  const [showAddressPicker, setShowAddressPicker] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: {
      code: "1",
      value: "",
    },
    country: "",
    state: "",
    county: "",
    city: "",
    pincode: "",
    street: "",
  });
  const [lockedProfileFields, setLockedProfileFields] = useState({
    firstName: false,
    lastName: false,
    email: false,
  });
  const [resolvedAddressNames, setResolvedAddressNames] = useState({
    county: "",
    city: "",
  });
  const [emailVerificationToken, setEmailVerificationToken] = useState("");
  const [isVerifiedEmailFlow, setIsVerifiedEmailFlow] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResolvingAddress, setIsResolvingAddress] = useState(false);
  const [mobileView, setMobileView] = useState(false);
  const [profileImagePreview, setProfileImagePreview] = useState("");
  const [profileImageFileName, setProfileImageFileName] = useState("");
  const [profileImageError, setProfileImageError] = useState("");
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const profileImageInputRef = useRef(null);
  const latestStateUuidRef = useRef("");
  const classes = useGetSignupScreenStyles({
    stepNo: 2,
    error: Boolean(error),
  });

  useEffect(() => {
    latestStateUuidRef.current = formData.state || "";
  }, [formData.state]);

  const loadStateOptions = useCallback(async (countryUuid) => {
    if (!countryUuid) {
      setStateOptions([]);
      return;
    }

    try {
      const result = await fetchStatesByCountry(countryUuid);
      setStateOptions(result);
    } catch (_error) {
      setStateOptions([]);
    }
  }, []);

  const hydrateFromStoredUser = useCallback((storedUser) => {
    if (!storedUser) {
      return;
    }

    setLockedProfileFields({
      firstName: hasProfileFieldValue(storedUser?.firstName),
      lastName: hasProfileFieldValue(storedUser?.lastName),
      email: hasProfileFieldValue(storedUser?.primaryEmail),
    });

    setFormData((current) => ({
      ...current,
      firstName: storedUser?.firstName || current.firstName,
      lastName: storedUser?.lastName || current.lastName,
      email: storedUser?.primaryEmail || current.email,
      phone:
        current.phone?.value || current.phone?.code !== "1"
          ? current.phone
          : getPhoneInputValue(storedUser),
      country: storedUser?.address?.country || current.country,
      state: storedUser?.address?.state || current.state,
      county: storedUser?.address?.county || current.county,
      city: storedUser?.address?.city || current.city,
      pincode: storedUser?.address?.zip || current.pincode,
    }));
    setResolvedAddressNames((current) => ({
      county: current.county || storedUser?.address?.county_name || "",
      city: current.city || storedUser?.address?.city_name || "",
    }));
    setProfileImagePreview(
      buildProfileImageUrl(storedUser?.avatar || storedUser?.profile_pic || ""),
    );
    setProfileImageFileName(storedUser?.profile_pic || "");
  }, []);

  useEffect(() => {
    const setResponsiveness = () => {
      setMobileView(window.innerWidth < 767);
    };

    setResponsiveness();
    window.addEventListener("resize", setResponsiveness);

    return () => {
      window.removeEventListener("resize", setResponsiveness);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const verifiedEmailToken = params.get("email_verification_token") || "";
    const prefillEmail = params.get("prefill_email") || "";
    const isVerifiedEmailContext = Boolean(verifiedEmailToken || prefillEmail);

    setIsVerifiedEmailFlow(isVerifiedEmailContext);
    setEmailVerificationToken(verifiedEmailToken);

    if (prefillEmail) {
      setLockedProfileFields((current) => ({
        ...current,
        email: true,
      }));
      setFormData((current) => ({
        ...current,
        email: prefillEmail,
      }));
    }
  }, [location.search]);

  useEffect(() => {
    const storedUser = readStoredUser();
    const token = readStoredToken();

    if (isVerifiedEmailFlow) {
      return;
    }

    if (!storedUser?.uuid || !token) {
      navigate("/signup", { replace: true });
      return;
    }

    if (!isProfileCompletionPending(storedUser)) {
      navigate("/home", { replace: true });
      return;
    }

    hydrateFromStoredUser(storedUser);
  }, [hydrateFromStoredUser, isVerifiedEmailFlow, navigate]);

  useEffect(() => {
    loadStateOptions(formData.country);
  }, [formData.country, loadStateOptions]);

  const setFieldValue = useCallback((field, value, options = {}) => {
    const preservePickedAddress = Boolean(options?.preservePickedAddress);

    if (field === "country" || field === "state") {
      if (!preservePickedAddress) {
        setPickedAddress(null);
        setError("");
        setResolvedAddressNames({
          county: "",
          city: "",
        });
      }
    }

    setFormData((current) => {
      const next = {
        ...current,
        [field]: value,
      };

      if (field === "country") {
        next.state = "";
        next.county = "";
        next.city = "";
        next.pincode = "";
        next.street = "";
      }

      if (field === "state") {
        next.county = "";
        next.city = "";
        next.pincode = "";
        next.street = "";
      }

      if (field === "county") {
        next.city = "";
        next.pincode = "";
        next.street = "";
      }

      return next;
    });

    if (field === "country") {
      setStateOptions([]);
    }
  }, []);

  const onFieldChange = useCallback(
    (field, value) => setFieldValue(field, value),
    [setFieldValue],
  );

  const selectedCountryLabel =
    COUNTRY1.find((item) => item.uuid === formData.country)?.name || "";
  const isAddressPickerDisabled = !formData.country || isResolvingAddress;

  const handleAddressConfirm = async (addressData) => {
    setPickedAddress(addressData);
    setShowAddressPicker(false);
    setError("");

    if (process.env.NODE_ENV !== "production") {
      console.log("[CompleteProfile][UseAddress]", {
        "state-dropdown": formData.state || "",
        regionName: addressData?.stateName || "",
      });
    }

    setIsResolvingAddress(true);
    try {
      // Always clear previous mapping so we never submit stale address parts for a new address.
      setResolvedAddressNames({
        county: "",
        city: "",
      });

      // Auto-fill country by matching label to COUNTRY1 constant (currently only USA).
      let nextCountryUuid = formData.country;
      if (addressData?.country) {
        const countryMatch = COUNTRY1.find(
          (c) =>
            c.name.toLowerCase() ===
            String(addressData.country).toLowerCase().trim(),
        );
        if (countryMatch?.uuid && countryMatch.uuid !== formData.country) {
          nextCountryUuid = countryMatch.uuid;
        }
      }

      const regionName = String(addressData?.stateName || "").trim();
      const regionCode = String(addressData?.state || "").trim();
      const pickedCountyName = String(addressData?.county || "").trim();
      const pickedCityName = String(
        pickCityCandidate(addressData) || "",
      ).trim();
      const pickedStreet = String(
        addressData?.displayLine1 ||
          addressData?.street ||
          addressData?.address1 ||
          "",
      ).trim();
      const zip5 = String(addressData?.zip || "").match(/\d{5}/)?.[0] || "";

      // Clear the selected state immediately when a new address is picked.
      setFormData((current) => ({
        ...current,
        country: nextCountryUuid,
        state: "",
        county: "",
        city: "",
        pincode: "",
        street: "",
      }));
      setStateOptions([]);

      if (pickedCityName) {
        setResolvedAddressNames((current) => ({
          ...current,
          city: pickedCityName,
        }));
      }

      // Fetch State list and match Region.Name -> state.name using exact or close name matching.
      const states = await fetchStatesByCountry(nextCountryUuid);
      setStateOptions(Array.isArray(states) ? states : []);

      const stateMatch = resolveStateMatch(states, regionName, regionCode);

      if (!stateMatch?.uuid) {
        setError(
          "Selected address state is not available. Please select state again.",
        );
        return;
      }

      if (process.env.NODE_ENV !== "production") {
        console.log("[CompleteProfile][StateUUIDUpdate]", {
          previousStateUuid: latestStateUuidRef.current,
          updatedStateUuid: stateMatch.uuid,
          regionName,
          regionCode,
        });
      }

      setFormData((current) => ({
        ...current,
        country: nextCountryUuid,
        state: stateMatch.uuid,
        county: "",
        city: "",
        pincode: zip5,
        street: pickedStreet,
      }));

      if (!pickedCityName) {
        setError("Pick an address with a city.");
        return;
      }

      if (!pickedCountyName) {
        setError("Pick an address with a county.");
        return;
      }

      const token = readStoredToken();
      const countyMatch = await findOrCreateCounty(
        token,
        stateMatch.uuid,
        pickedCountyName,
      );

      if (countyMatch?.uuid) {
        setFormData((current) => ({
          ...current,
          county: countyMatch.uuid,
        }));
      }

      if (!countyMatch?.uuid) {
        setError(
          "Selected address county is not available. Please select another address.",
        );
        return;
      }

      const cityMatch = await findOrCreateCity(
        token,
        countyMatch.uuid,
        pickedCityName,
      );

      if (!cityMatch?.uuid) {
        setError(
          "Selected address city is not available. Please select address again.",
        );
        return;
      }

      setFormData((current) => ({
        ...current,
        county: countyMatch?.uuid || "",
        city: cityMatch.uuid,
        pincode: zip5,
        street: pickedStreet,
      }));
      setResolvedAddressNames({
        county: countyMatch?.name || pickedCountyName,
        city: cityMatch.name || pickedCityName,
      });
    } catch (resolveError) {
      setError(
        resolveError?.message ||
          "Selected address could not be resolved. Please select address again.",
      );
    } finally {
      setIsResolvingAddress(false);
    }
  };

  // Keep ZIP in sync with the picked address (auto-fill may get cleared when state is auto-set).
  useEffect(() => {
    if (!pickedAddress?.zip) return;
    const pickedZip = String(pickedAddress.zip || "");
    const zip5 = pickedZip.match(/\d{5}/)?.[0] || "";
    if (!zip5) return;
    if (String(formData.pincode || "") === zip5) return;

    setFieldValue("pincode", zip5, {
      preservePickedAddress: true,
    });
  }, [pickedAddress, formData.pincode, setFieldValue]);

  const handleProfileImageSelection = async (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!String(file.type || "").startsWith("image/")) {
      setProfileImageError("Please upload a valid image file.");
      event.target.value = "";
      return;
    }

    setProfileImageError("");
    setIsUploadingImage(true);

    try {
      const preview = await readFileAsDataUrl(file);
      const body = new FormData();
      body.append("file", file);

      const response = await fetch(`${baseUrl}/user/upload/file`, {
        method: "POST",
        body,
      });
      const responseText = await response.text();

      if (response.status !== 200) {
        throw new Error(responseText || "Profile photo could not be uploaded.");
      }

      const result = responseText ? JSON.parse(responseText) : {};
      setProfileImagePreview(preview);
      setProfileImageFileName(result?.image?.filename || "");
    } catch (uploadError) {
      setProfileImageError(
        uploadError?.message || "Profile photo could not be uploaded.",
      );
    } finally {
      setIsUploadingImage(false);
      event.target.value = "";
    }
  };

  const validate = (overrides = {}) => {
    const data = { ...formData, ...overrides };
    const firstName = String(data.firstName || "").trim();
    const lastName = String(data.lastName || "").trim();
    const email = String(data.email || "").trim();
    const phoneValue = String(data.phone?.value || "").trim();
    const street = String(data.street || "").trim();
    const country = String(data.country || "").trim();
    const state = String(data.state || "").trim();
    const county = String(data.county || "").trim();
    const city = String(data.city || "").trim();
    const pincode = String(data.pincode || "").trim();

    const firstNameError = getProfileNameError(firstName, "First name");
    if (firstNameError) return firstNameError;

    const lastNameError = getProfileNameError(lastName, "Last name");
    if (lastNameError) return lastNameError;

    if (!email) {
      return "Enter your email address.";
    }

    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      return "Enter a valid email address.";
    }

    if (phoneValue && !/^\d{10}$/.test(phoneValue)) {
      return "Enter a valid 10 digit phone number.";
    }

    if (isVerifiedEmailFlow) {
      if (!emailVerificationToken) {
        return "Email verification expired. Please verify your email again.";
      }
    }

    if (isResolvingAddress) {
      return "Please wait while the selected address is being resolved.";
    }

    if (isUploadingImage) {
      return "Please wait for the profile photo upload to finish.";
    }

    if (!country) {
      return "Select your country.";
    }

    if (!state) {
      return "Select your state.";
    }

    if (!pickedAddress) {
      return "Pick an address to continue.";
    }

    if (!street) {
      return "Pick an address with a street address.";
    }

    if (!county) {
      return "Pick an address with a county.";
    }

    if (!city) {
      return "Pick an address with a city.";
    }

    if (!/^\d{5}$/.test(pincode)) {
      return "Pick an address with a valid 5 digit ZIP code.";
    }

    return "";
  };

  const submitProfile = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      let response;
      let requestUrl = "";
      let requestMethod = "";
      let token = "";
      let storedUser = null;
      const pickedPosition =
        pickedAddress?.lat != null && pickedAddress?.lng != null
          ? {
              type: "Point",
              coordinates: [pickedAddress.lng, pickedAddress.lat],
            }
          : undefined;

      const payloadCountry = formData.country;
      const payloadState = formData.state;
      const payloadCity = String(formData.city || "").trim();
      const payloadPincode = String(formData.pincode || "").trim();
      const payloadFirstName = String(formData.firstName || "").trim();
      const payloadLastName = String(formData.lastName || "").trim();
      const payloadEmail = String(formData.email || "").trim();
      const payloadCounty = String(formData.county || "").trim();
      const payloadMobile = formData.phone?.value
        ? `${String(formData.phone?.code || "").replace("+", "")}${String(
            formData.phone?.value || "",
          ).trim()}`
        : "";
      const optionalMobilePayload = payloadMobile
        ? { mobile: payloadMobile }
        : {};

      if (isVerifiedEmailFlow) {
        requestUrl = `${baseUrl}/user/register/complete-profile`;
        requestMethod = "POST";
        response = await fetch(requestUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            firstName: payloadFirstName,
            lastName: payloadLastName,
            email: payloadEmail,
            country: payloadCountry,
            state: payloadState,
            county: payloadCounty,
            city: payloadCity,
            pincode: payloadPincode,
            profile_pic: profileImageFileName,
            email_verification_token: emailVerificationToken,
            ...optionalMobilePayload,
            ...(pickedPosition ? { position: pickedPosition } : {}),
          }),
        });
      } else {
        storedUser = readStoredUser();
        token = readStoredToken();

        if (!storedUser?.uuid || !token) {
          setError("Your session expired. Please sign in with Google again.");
          return;
        }

        requestUrl = `${baseUrl}/user/complete/profile/${storedUser.uuid}`;
        requestMethod = "PUT";
        response = await fetch(requestUrl, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-auth-token": token,
          },
          body: JSON.stringify({
            uuid: storedUser.uuid,
            firstName: payloadFirstName,
            lastName: payloadLastName,
            email: payloadEmail,
            country: payloadCountry,
            state: payloadState,
            county: payloadCounty,
            city: payloadCity,
            pincode: payloadPincode,
            profile_pic: profileImageFileName,
            ...optionalMobilePayload,
            ...(pickedPosition ? { position: pickedPosition } : {}),
          }),
        });
      }

      const responseText = await response.text();
      if (response.status !== 200) {
        let message = responseText || "Profile could not be completed.";
        try {
          const maybeJson = responseText ? JSON.parse(responseText) : null;
          message = maybeJson?.message || maybeJson?.error || message;
        } catch (_error) {
          // ignore non-json error bodies
        }

        throw new Error(
          `${message} (HTTP ${response.status} ${requestMethod} ${requestUrl})`,
        );
      }

      const result = JSON.parse(responseText);
      const bingoDetail = result?.bingoDetail;
      const profileImageUrl = buildProfileImageUrl(
        profileImageFileName ||
          result?.data?.profile_pic ||
          storedUser?.avatar ||
          storedUser?.profile_pic ||
          "",
      );
      const sessionData = {
        ...result?.data,
        avatar: profileImageUrl,
        profile_pic: profileImageUrl,
        mobile: result?.data?.mobile || payloadMobile,
        token: result?.token || token,
        dInfoUserEmail:
          bingoDetail?.dInfoUserEmail || storedUser?.dInfoUserEmail,
        dInfoUserId: bingoDetail?.dInfoUserId || storedUser?.dInfoUserId,
        dInfoUserType: bingoDetail?.dInfoUserType || storedUser?.dInfoUserType,
      };

      persistUserSession({
        data: sessionData,
        setUserDetails,
        setCookie,
      });
      navigate("/home", { replace: true });
    } catch (submitError) {
      setError(
        submitError?.message ||
          "Profile could not be completed. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <AppBar />
      <div css={classes.pageWrapper}>
        {!mobileView && (
          <div onClick={() => navigate("/signup")} css={classes.backButton}>
            <img css={classes.backIcon} src={Previous} alt="" />
            Back
          </div>
        )}
        {mobileView && (
          <div
            onClick={() => navigate("/signup")}
            css={classes.backButton}
            style={{ margin: "16px 0 0 16px" }}
          >
            <img css={classes.backIcon} src={Previous} alt="" />
            Back
          </div>
        )}
        <div css={classes.signUpFormContainer}>
          {!mobileView && (
            <img
              src={signUpSVG}
              css={classes.illustration}
              alt="profile completion illustration"
            />
          )}
          <form
            css={[classes.signupForm, classes.profileCompletionForm]}
            onSubmit={(event) => {
              event.preventDefault();
              submitProfile();
            }}
          >
            {mobileView ? (
              <h4 css={classes.formHeading}>
                Complete your Money-Mails profile
              </h4>
            ) : (
              <h2 css={classes.formHeading}>
                Complete your Money-Mails profile
              </h2>
            )}
            <div css={classes.socialSignupBlock}>
              <p css={classes.socialHelper}>
                {isVerifiedEmailFlow
                  ? "Add your location so Money Mails can finish creating your account."
                  : "Add your location so Money Mails can show relevant stores and mail data."}
              </p>
            </div>
            <div css={classes.profileCompletionContent}>
              <div css={classes.profilePhotoPanel}>
                <div css={classes.profilePhotoHeader}>
                  <h3 css={classes.profilePhotoTitle}>Profile Photo</h3>
                  <span css={classes.profilePhotoOptional}>Optional</span>
                </div>
                <p css={classes.profilePhotoDescription}>
                  Add a profile picture now or skip it and finish your account
                  setup. You can change it later.
                </p>
                <div css={classes.profilePhotoPreviewFrame}>
                  {profileImagePreview ? (
                    <img
                      src={profileImagePreview}
                      alt="Profile preview"
                      css={classes.profilePhotoPreviewImage}
                    />
                  ) : (
                    <div css={classes.profilePhotoEmptyState}>
                      <span css={classes.profilePhotoEmptyIcon}>+</span>
                      <span css={classes.profilePhotoEmptyText}>
                        Upload your photo
                      </span>
                    </div>
                  )}
                </div>
                <input
                  ref={profileImageInputRef}
                  css={classes.profilePhotoInput}
                  type="file"
                  accept="image/*"
                  onChange={handleProfileImageSelection}
                />
                <Button
                  type="button"
                  variant="contained"
                  color="primary"
                  css={classes.profilePhotoButton}
                  onClick={() => profileImageInputRef.current?.click()}
                  disabled={isUploadingImage}
                >
                  {isUploadingImage
                    ? "Uploading..."
                    : profileImagePreview
                      ? "Change Photo"
                      : "Upload Photo"}
                </Button>
                <p css={classes.profilePhotoHint}>
                  Supported: image files such as JPG and PNG.
                </p>
                {profileImageError && (
                  <div css={classes.authErrorBox}>{profileImageError}</div>
                )}
              </div>
              <div css={classes.profileFieldsColumn}>
                <div css={classes.formFieldsWrapper}>
                  <div css={classes.formFieldsGroup}>
                    <div css={classes.sectionLabel}>Personal Details</div>
                    <TextBox
                      variant="outline"
                      label="First Name"
                      color="secondary"
                      fullWidth
                      required
                      disabled={lockedProfileFields.firstName}
                      id="firstName"
                      value={formData.firstName}
                      onChange={(event, value) =>
                        onFieldChange(event.target.id, value)
                      }
                    />
                    <TextBox
                      variant="outline"
                      label="Last Name"
                      color="secondary"
                      fullWidth
                      required
                      disabled={lockedProfileFields.lastName}
                      id="lastName"
                      value={formData.lastName}
                      onChange={(event, value) =>
                        onFieldChange(event.target.id, value)
                      }
                    />
                    <TextBox
                      variant="outline"
                      label="Primary Email"
                      color="secondary"
                      type="email"
                      fullWidth
                      required
                      disabled={lockedProfileFields.email}
                      id="email"
                      value={formData.email}
                      onChange={(event, value) =>
                        onFieldChange(event.target.id, value)
                      }
                    />
                    <TextBox
                      variant="outline"
                      label="Phone Number"
                      color="secondary"
                      type="tel"
                      fullWidth
                      id="phone"
                      value={formData.phone?.value || ""}
                      defaultCountryCode={formData.phone?.code || "1"}
                      onChange={(_event, value) =>
                        onFieldChange("phone", value)
                      }
                    />

                    <div css={classes.sectionLabel}>Address Details</div>
                    <Dropdown
                      id="country"
                      label="Country*"
                      lists={COUNTRY1}
                      renderKey="name"
                      renderValue="uuid"
                      value={formData.country}
                      onChange={(_event, value) =>
                        onFieldChange("country", value)
                      }
                    />
                    <Dropdown
                      id="state"
                      label="State*"
                      lists={stateOptions}
                      renderKey="name"
                      renderValue="uuid"
                      value={formData.state}
                      onChange={(_event, value) =>
                        onFieldChange("state", value)
                      }
                    />
                    {pickedAddress ? (
                      <div
                        style={{
                          border: "1px solid #dbe3f0",
                          borderRadius: 12,
                          background: "#f8fbff",
                          padding: 14,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 12,
                            flexWrap: "wrap",
                          }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 13,
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {pickedAddress.street ||
                                pickedAddress.displayLine1 ||
                                pickedAddress.address1}
                            </div>
                            {pickedAddress.displayLine2 && (
                              <div style={{ fontSize: 12, color: "#6c757d" }}>
                                {pickedAddress.displayLine2}
                              </div>
                            )}
                          </div>
                          <Button
                            type="button"
                            variant="outlined"
                            color="primary"
                            onClick={() => setShowAddressPicker(true)}
                          >
                            Change Address
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <button
                          type="button"
                          disabled={isAddressPickerDisabled}
                          onClick={() => setShowAddressPicker(true)}
                          style={{
                            display: "flex",
                            width: "100%",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                            padding: "18px 16px",
                            border: `1.5px dashed ${
                              isAddressPickerDisabled ? "#d0d5dd" : "#1b6ef3"
                            }`,
                            borderRadius: 14,
                            background: isAddressPickerDisabled
                              ? "#f8f9fb"
                              : "#f7faff",
                            color: isAddressPickerDisabled
                              ? "#98a2b3"
                              : "#1b6ef3",
                            cursor: isAddressPickerDisabled
                              ? "not-allowed"
                              : "pointer",
                            fontSize: 14,
                            fontWeight: 600,
                            textAlign: "center",
                          }}
                        >
                          <LocationOnOutlinedIcon
                            sx={{ fontSize: 18, color: "inherit" }}
                          />
                          <span>Enter Current ZIP Only</span>
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: "4px 10px",
                              borderRadius: 999,
                              background: "#f2f4f7",
                              color: "#667085",
                              fontSize: 11,
                              fontWeight: 600,
                              lineHeight: 1.4,
                            }}
                          >
                            Current ZIP cannot be changed, contact MoneyMails
                            Support
                          </div>
                        </button>
                      </div>
                    )}
                    {pickedAddress && (
                      <>
                        <TextBox
                          variant="outline"
                          label="Street"
                          color="secondary"
                          fullWidth
                          disabled
                          id="pickedStreet"
                          value={
                            formData.street ||
                            pickedAddress.displayLine1 ||
                            pickedAddress.street ||
                            pickedAddress.address1 ||
                            ""
                          }
                          fieldBackgroundColor="#f2f2f2"
                        />
                        <div style={{ display: "flex", gap: 12 }}>
                          <div style={{ flex: 2 }}>
                            <TextBox
                              variant="outline"
                              label="City"
                              color="secondary"
                              fullWidth
                              disabled
                              id="pickedCity"
                              value={
                                resolvedAddressNames.city ||
                                pickCityCandidate(pickedAddress)
                              }
                              fieldBackgroundColor="#f2f2f2"
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <TextBox
                              variant="outline"
                              label="ZIP"
                              color="secondary"
                              fullWidth
                              disabled
                              id="pickedZip"
                              value={
                                formData.pincode ||
                                String(pickedAddress.zip || "").match(
                                  /\d{5}/,
                                )?.[0] ||
                                pickedAddress.zip ||
                                ""
                              }
                              fieldBackgroundColor="#f2f2f2"
                            />
                          </div>
                        </div>
                      </>
                    )}
                    {error && <div css={classes.authErrorBox}>{error}</div>}
                  </div>
                </div>
              </div>
            </div>
            <div css={classes.footerBtn}>
              {mobileView && (
                <Button
                  type="button"
                  variant="text"
                  color="secondary"
                  css={classes.submitButton}
                  onClick={() => navigate("/signup")}
                >
                  Back
                </Button>
              )}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                css={classes.submitButton}
                sx={{ minWidth: 140, marginLeft: "auto" }}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : "Complete Profile"}
              </Button>
            </div>
          </form>
        </div>
      </div>
      <AddressPickerModal
        show={showAddressPicker}
        onHide={() => setShowAddressPicker(false)}
        onConfirm={handleAddressConfirm}
        countryLabel={selectedCountryLabel}
      />
    </>
  );
};

export default ProfileCompletion;
